import Component from "@glimmer/component";
import { action } from "@ember/object";
import { cached } from "@glimmer/tracking";
import { service } from "@ember/service";
import { array, fn } from "@ember/helper";
import { on } from "@ember/modifier";
import { task } from "ember-concurrency";
import taskHelper from "ember-concurrency/helpers/task";
import { t } from "ember-intl";
import { and, not } from "ember-truth-helpers";
import { saveRecord } from "@warp-drive/legacy/compat/builders";
import destroyRecord from "glesys-controlpanel/utils/destroy-record";
import canManage from "glesys-controlpanel/helpers/can-manage";
import modal from "glesys-controlpanel/helpers/modal";
import PageContent from "glesys-controlpanel/components/page-content";
import PageContentServiceIntro from "glesys-controlpanel/components/page-content/service-intro";
import GButtonIcon from "glesys-controlpanel/components/g-button/icon";
import IconsPlus from "glesys-controlpanel/components/icons/plus";
import DropdownItem from "glesys-controlpanel/components/dropdown/item";
import DropdownDestructiveItem from "glesys-controlpanel/components/dropdown/destructive-item";
import EmptyState from "glesys-controlpanel/components/empty-state";
import Entity from "glesys-controlpanel/components/entity";
import EntityField from "glesys-controlpanel/components/entity/fields/field";
import EntityList from "glesys-controlpanel/components/entity-list";
import CostPlain from "glesys-controlpanel/components/cost/plain";
import IconsSidebarNetworking from "glesys-controlpanel/components/icons/sidebar/networking";

export default class VpnOverview extends Component {
  @service modal;
  @service store;
  @service cost;

  get project() {
    return this.args.model.project;
  }

  get vpnUsers() {
    return this.args.model.vpnUsers;
  }

  @cached
  get vpnUsersFiltered() {
    return this.vpnUsers.filter((user) => !user.isNew);
  }

  get vpnCost() {
    return this.cost.forVpnUser();
  }

  createVpnUserTask = task(async (username, password) => {
    let project = this.project;
    let projectNumber = project.get("accountname");

    username = `${projectNumber}_${username}`;

    let vpnUser = this.store.createRecord("vpn-user", {
      username,
      password,
      project,
      enabled: true,
    });

    try {
      await this.store.request(saveRecord(vpnUser));
    } catch (error) {
      vpnUser.unloadRecord();
      throw error;
    }
  });

  changeVpnUserPasswordTask = task(async (vpnUser, password) => {
    vpnUser.set("password", password);
    await this.store.request(saveRecord(vpnUser));
  });

  deleteVpnUser = task(async (vpnUser) => {
    await destroyRecord(vpnUser);
  });

  toggleEnabledTask = task(async (vpnUser) => {
    vpnUser.set("enabled", !vpnUser.enabled);

    try {
      await this.store.request(saveRecord(vpnUser));
    } catch (error) {
      vpnUser.rollbackAttributes();
      throw error;
    }
  });

  @action
  openChangePasswordModal(vpnUser) {
    let task = this.changeVpnUserPasswordTask;
    this.modal.open("modals/vpn-user-change-password", { task, vpnUser });
  }

  <template>
    <PageContent>
      <PageContentServiceIntro @title={{t "vpn.page-title"}}>
        {{#if (and (canManage) this.vpnUsersFiltered)}}
          <GButtonIcon
            data-test-page-title-create
            @display="default"
            @color="green"
            {{on "click" (modal "modals/vpn-user-create" project=this.project task=this.createVpnUserTask)}}
          >
            <IconsPlus class="w-4 -ml-1 mr-1" />
            {{t "button.create"}}
          </GButtonIcon>
        {{/if}}
      </PageContentServiceIntro>

      <EntityList @items={{this.vpnUsersFiltered}}>
        <:default as |vpnUser|>
          <Entity @title={{vpnUser.username}} @status={{vpnUser.status}} @hideActions={{not (canManage)}}>
            <:fields>
              <EntityField data-test-vpn-user-cost @label={{t "label.cost"}}>
                <CostPlain @currency={{this.vpnCost.currency}} @price={{this.vpnCost.cost}} />
              </EntityField>
            </:fields>
            <:actions>
              <DropdownItem data-test-vpn-user-change-password {{on "click" (fn this.openChangePasswordModal vpnUser)}}>
                {{t "button.change-password"}}
              </DropdownItem>
              <DropdownItem
                data-test-vpn-user-toggle-enabled
                {{on "click" (fn this.toggleEnabledTask.perform vpnUser)}}
              >
                {{if vpnUser.enabled (t "vpn.status-disable") (t "vpn.status-enable")}}
              </DropdownItem>
              <DropdownDestructiveItem
                data-test-vpn-delete-button
                {{on
                  "click"
                  (modal
                    "modals/organism-delete"
                    entityKey=vpnUser.username
                    entity=vpnUser.username
                    task=(taskHelper this.deleteVpnUser vpnUser)
                  )
                }}
              >
                {{t "label.delete"}}
              </DropdownDestructiveItem>
            </:actions>
          </Entity>
        </:default>
        <:empty>
          <EmptyState
            @icon={{IconsSidebarNetworking}}
            @title={{t "vpn.empty-state.title"}}
            @description={{t "vpn.empty-state.description"}}
            @points={{array
              (t "vpn.empty-state.usp-list.known-addresses")
              (t "vpn.empty-state.usp-list.protocol")
              (t "vpn.empty-state.usp-list.ipmi")
            }}
            @learnMoreUrl="https://docs.glesys.com/products/connectivity/network-services/virtual-private-network-vpn"
            @learnMoreAriaLabel={{t "vpn.empty-state.learn-more-aria-label"}}
            @createLabel={{t "vpn.empty-state.button"}}
            @onCreate={{if
              (canManage)
              (modal "modals/vpn-user-create" project=this.project task=this.createVpnUserTask)
            }}
          />
        </:empty>
      </EntityList>
    </PageContent>
  </template>
}
